import produce from 'immer';
import { QueueDashboardQueueSortBy } from 'lib-dashboard-types';
import { ReadSearchApiArg, SearchSortField } from 'lib-search-types';
import { useCallback, useMemo } from 'react';

import { getSortObjectFromString } from 'utils/url/getSortObjectFromString';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';
import { useCurrentUser } from 'utils/user';

/** The default queues page size. */
const defaultPageSize = 50;

type UseQueueDashboardParamsToApiArgResult = {
  /** Function to convert current params to ReadSearchApiArg<'queues'>. */
  readonly queueParamsToApiArg: () => ReadSearchApiArg<'queues'>;
  /** Function to convert sort string to SearchSort object */
  readonly queueSortStringToObject: (
    sort_by?: QueueDashboardQueueSortBy,
  ) => ReadSearchApiArg<'queues'>['sortBy'];
};

/**
 * A hook that provides methods for mapping the queue dashboard URL search
 * params to the Search API argument format for fetching queues.
 */
export const useQueueDashboardParamsToApiArg =
  (): UseQueueDashboardParamsToApiArgResult => {
    const { user } = useCurrentUser();
    const { params } = useAppSearchParams('/dashboard/queues');

    const queueSortStringToObject = useCallback<
      UseQueueDashboardParamsToApiArgResult['queueSortStringToObject']
    >(
      (sort_by) => {
        // Don't apply any default sorting when viewing favorites or custom queues and no explicit
        // sort_by parameter is set, in which case the sorting is determined by the queues order.
        if (!sort_by) {
          if (params.type === 'custom') return undefined;
          if (params.type === 'favourite' && params.id) return undefined;
        }

        const sort = getSortObjectFromString<SearchSortField<'queues'>>(
          sort_by ?? 'name:asc',
          ['name', 'priority'],
        );

        return sort
          ? { field: sort.key, order: sort.direction ?? 'asc' }
          : undefined;
      },
      [params.id, params.type],
    );

    const queueParamsToApiArg = useCallback<
      UseQueueDashboardParamsToApiArgResult['queueParamsToApiArg']
    >(() => {
      // Pagination is 1-based in the URL params and 0-based in the API
      const page = params.page ? parseInt(params.page) - 1 : 0;

      return produce<ReadSearchApiArg<'queues'>>(
        {
          scope: 'queues',
          query: params.q || undefined,
          filters: { strategy: 'all', filters: [] },
          sortBy: queueSortStringToObject(params.sort_by),
          page: page > 0 ? page : undefined,
          pageSize: defaultPageSize,
        },
        (draft) => {
          // Filter by agent assignment.
          if (params.type === 'agent') {
            draft.filters?.filters.push({
              field: 'agents',
              operator: 'includes_one',
              values: [{ value: user.id }],
            });
          }

          // Filter by custom queue IDs.
          if (params.type === 'custom' && params.queue) {
            draft.filters?.filters.push({
              field: 'id',
              operator: 'is_one',
              values: [params.queue].flat().map((id) => ({ value: id })),
            });
          }

          // Filter by archived status (not applied for custom or favourite views).
          if (params.type !== 'custom' && params.type !== 'favourite') {
            draft.filters?.filters.push({
              field: 'is_archived',
              operator: params.type === 'archived' ? 'equals' : 'not_equals',
              values: [{ value: 'true' }],
            });
          }
        },
      );
    }, [
      params.page,
      params.q,
      params.queue,
      params.sort_by,
      params.type,
      queueSortStringToObject,
      user.id,
    ]);

    return useMemo(
      () => ({ queueParamsToApiArg, queueSortStringToObject }),
      [queueParamsToApiArg, queueSortStringToObject],
    );
  };
